import { useEffect, useState } from 'react';

import { navGroups } from '../../app/nav';

// Quick access is auto-populated from how often each destination is visited.
// A single global tracker (installRouteTracker, wired in src/index.js) taps the
// SHARED history so it sees BOTH v2 and classic (v1) navigations — the v2 app
// tree only mounts on /v2/* routes, so it can't observe v1 visits on its own.
// Usage is stored per user + per machine in localStorage (no backend).

export type QuickLink = { label: string; path: string };

export const isV2Path = (path: string): boolean => path.startsWith('/v2');

// v2 destinations, flattened from the sidebar nav (single source of truth).
const V2_DESTINATIONS: QuickLink[] = navGroups.flatMap((g) =>
    g.items.map((i) => ({ label: i.label, path: i.to })),
);

// Destinations behind the admin-only nav gate, group- or item-level. Usage is
// read from localStorage, so someone who visited these before the gate landed
// would keep seeing tiles that now just bounce off AdminGate — filter them at
// read time rather than leaving dead shortcuts on the dashboard.
const ADMIN_ONLY_PATHS = new Set<string>(
    navGroups.flatMap((g) => g.items.filter((i) => g.adminOnly || i.adminOnly).map((i) => i.to)),
);

// Classic (v1) destinations, from the legacy OrchestraMenu, so v1 visits get a
// nice label and normalize to their section.
const V1_DESTINATIONS: QuickLink[] = [
    { label: 'Dashboard clasic', path: '/v1/dashboard' },
    { label: 'Comenzi', path: '/orders' },
    { label: 'Cosuri', path: '/carts' },
    { label: 'Produse (clasic)', path: '/products' },
    { label: 'Catalog', path: '/catalog' },
    { label: 'Carti', path: '/cards' },
    { label: 'Liste produse', path: '/product-lists' },
    { label: 'Liste categorii', path: '/category-lists' },
    { label: 'Utilizatori (clasic)', path: '/users' },
    { label: 'Oferte', path: '/offers' },
    { label: 'Vouchere', path: '/vouchers' },
    { label: 'Rapoarte', path: '/reports/new-reports' },
    { label: 'Rapoarte (legacy)', path: '/reports/legacy-reports' },
    { label: 'Calendar', path: '/calendar' },
    { label: 'Carusel', path: '/carousel' },
    { label: 'Inregistrari interes', path: '/interest-registrations' },
    { label: 'Atribute', path: '/attributes' },
    { label: 'Categorii', path: '/categories' },
    { label: 'Furnizori', path: '/suppliers' },
    { label: 'Operatii stoc', path: '/stock-operations' },
    { label: 'Buffer inventar', path: '/inventory-buffer' },
    { label: 'Tipuri plata', path: '/payment-types' },
    { label: 'Conturi stoc', path: '/stock-accounts' },
    { label: 'Manifeste expediere', path: '/shipment-manifests' },
];

// Longest path first so prefix matching picks the most specific destination
// (e.g. /reports/new-reports wins over a shorter /reports).
const DESTINATIONS: QuickLink[] = [...V2_DESTINATIONS, ...V1_DESTINATIONS].sort(
    (a, b) => b.path.length - a.path.length,
);

// Don't surface a shortcut to the dashboard itself (that's where this panel lives).
const EXCLUDED = new Set<string>(['/v2/dashboard']);

// Map a visited URL to the destination it sits under, so detail pages count
// toward their section (/orders/123 -> /orders, /v2/products/5/edit -> /v2/products).
function resolveDestination(pathname: string): QuickLink | null {
    const clean = pathname.split('?')[0].split('#')[0];
    for (const d of DESTINATIONS) {
        if (clean === d.path || clean.startsWith(d.path + '/')) return d;
    }
    return null;
}

type UsageEntry = { label: string; path: string; count: number; lastVisited: number };
type UsageMap = Record<string, UsageEntry>;

// Per user + per machine: localStorage is the machine; the user id scopes the key.
const keyFor = (userId?: number | null): string => `v2:route-usage:${userId ?? 'anon'}`;

function readUsage(userId?: number | null): UsageMap {
    try {
        const raw = window.localStorage.getItem(keyFor(userId));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as UsageMap) : {};
    } catch {
        return {};
    }
}

// Record one visit. No-op for unknown destinations and anonymous sessions.
export function recordVisit(userId: number | null | undefined, pathname: string): void {
    if (userId == null) return;
    const dest = resolveDestination(pathname);
    if (!dest) return;
    const map = readUsage(userId);
    const prev = map[dest.path];
    map[dest.path] = {
        label: dest.label,
        path: dest.path,
        count: (prev?.count ?? 0) + 1,
        lastVisited: Date.now(),
    };
    try {
        window.localStorage.setItem(keyFor(userId), JSON.stringify(map));
    } catch {
        /* storage unavailable/full — drop this visit */
    }
}

// Most-used destinations, by count then recency, excluding the dashboard itself
// and — for non-admins — anything behind the admin-only gate. `isAdmin` defaults
// to false so a caller that forgets it hides too much rather than too little.
export function getMostUsed(
    userId: number | null | undefined,
    limit = 8,
    isAdmin = false,
): QuickLink[] {
    return Object.values(readUsage(userId))
        .filter((e) => !EXCLUDED.has(e.path))
        .filter((e) => isAdmin || !ADMIN_ONLY_PATHS.has(e.path))
        .sort((a, b) => b.count - a.count || b.lastVisited - a.lastVisited)
        .slice(0, limit)
        .map((e) => ({ label: e.label, path: e.path }));
}

type HistoryLike = { listen: (cb: (location: { pathname: string }) => void) => unknown };
let installed = false;

// Install once at app boot (src/index.js) against the SHARED history so both v1
// and v2 navigations are recorded. `getUserId` is read lazily on each nav so the
// tracker keys to whoever is logged in at the time.
export function installRouteTracker(
    history: HistoryLike,
    getUserId: () => number | null | undefined,
): void {
    if (installed) return;
    installed = true;
    history.listen((location) => {
        try {
            recordVisit(getUserId(), location.pathname);
        } catch {
            /* never let tracking break navigation */
        }
    });
}

// Reads the current most-used list. Re-reads when the dashboard mounts (i.e.
// each time the user returns to it), which is when fresh usage matters.
export function useMostUsed(
    userId: number | null | undefined,
    limit = 8,
    isAdmin = false,
): QuickLink[] {
    const [items, setItems] = useState<QuickLink[]>([]);
    useEffect(() => {
        setItems(getMostUsed(userId, limit, isAdmin));
    }, [userId, limit, isAdmin]);
    return items;
}
